import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import _isNull from 'lodash/isNull';
import { SelectProps } from 'antd';
import classNames from 'classnames';

import SelectWrapper from '../select/select';

export interface Translations {
	selectItem: string;
	unassigned: string;
}

interface RequiredOptionFields {
	id: string | number;
	name: string;
}

export interface AssignUserProps<T> extends SelectProps<T> {
	items: T[];
	selectedItemId: number;
	onSelectedItem: (itemId?: number) => void;
	translations: Translations;
	enableUnassign?: boolean;
	renderOption?: (item: T) => React.ReactNode;
	open?: boolean;
	disabled?: boolean;
	loading?: boolean;
}

const AssignComponent = <T extends RequiredOptionFields>({
	items,
	selectedItemId,
	onSelectedItem,
	enableUnassign = true,
	translations,
	renderOption,
	open,
	disabled,
	loading = false,
	dropdownClassName
}: AssignUserProps<T>) => {
	const selectWrapperRef = useRef<SelectWrapper>();
	const unassignedItem = { id: -1, name: translations.unassigned };
	const [selectOptions, setSelectOptions] = useState([]);
	const [value, setValue] = useState(selectedItemId);

	useEffect(() => {
		selectWrapperRef.current.getSelectRef()[open ? 'focus' : 'blur']();
	}, [open]);

	useEffect(() => {
		if (_isNull(selectedItemId)) {
			setValue(undefined);
		} else {
			setValue(selectedItemId);
		}
	}, [selectedItemId]);

	useEffect(() => {
		if (enableUnassign) {
			setSelectOptions([unassignedItem, ...items]);
		} else {
			setSelectOptions([...items]);
		}
	}, [items, enableUnassign]);

	const handleItemSelected = useCallback(
		itemId => {
			if (itemId === unassignedItem.id) {
				onSelectedItem && onSelectedItem();
			} else {
				onSelectedItem && onSelectedItem(itemId);
			}
		},
		[onSelectedItem, unassignedItem]
	);

	const transformedOptions = useMemo(() => {
		return renderOption
			? selectOptions.map(item => {
					return { ...item, render: renderOption(item) };
			  })
			: selectOptions;
	}, [renderOption, selectOptions]);

	return (
		<div className="assign-component-container">
			<SelectWrapper
				showSearch
				disabled={disabled}
				className="assign-component-select"
				placeholder={translations.selectItem}
				onSelect={handleItemSelected}
				options={transformedOptions}
				value={value}
				popupClassName={classNames('assign-component-dropdown', dropdownClassName)}
				filterOption={(input, option) =>
					option.props.title.toString().toLowerCase().includes(input.toLowerCase())
				}
				open={open}
				ref={selectWrapperRef}
				loading={loading}
			/>
		</div>
	);
};

export default AssignComponent;
